import { useEffect, useState } from "react";
import { api, fmtINR } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  UsersThree, Receipt, IdentificationCard, TrendUp, TrendDown,
  Warning, Calculator, MapPin, Briefcase,
} from "@phosphor-icons/react";

const Stat = ({ icon: Icon, label, value, sub, accent }) => (
  <Card className="rounded-sm p-6 border-border" data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="label-caps">{label}</p>
        <p className="font-heading text-3xl font-bold mt-2 tracking-tight">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </div>
      <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${accent}`}>
        <Icon size={20} weight="bold" />
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [geo, setGeo] = useState("city");

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setS(r.data));
  }, []);

  if (!s) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const geoRows = (geo === "city" ? s.orders_by_city : s.orders_by_state) || [];
  const maxOrders = Math.max(1, ...geoRows.map((r) => r.orders));

  return (
    <>
      <PageHeader title="Dashboard" subtitle="Operational & finance overview" />
      <div className="p-8 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <Stat icon={UsersThree} label="Partners" value={s.partners_total}
            sub={`${s.partners_active} active`} accent="bg-blue-100 text-blue-700" />
          <Stat icon={Receipt} label="Active Ratecards" value={s.ratecards_active}
            accent="bg-violet-100 text-violet-700" />
          <Stat icon={IdentificationCard} label="Customers" value={s.customers_total}
            accent="bg-emerald-100 text-emerald-700" />
          <Stat icon={Calculator} label="Transactions" value={s.txn_count}
            sub={`${s.pending_docs} missing docs`} accent="bg-amber-100 text-amber-700" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <Stat icon={TrendUp} label="Total Sales" value={fmtINR(s.sales_total)} accent="bg-green-100 text-green-700" />
          <Stat icon={TrendDown} label="Total Purchases" value={fmtINR(s.purchases_total)} accent="bg-rose-100 text-rose-700" />
          <Stat icon={TrendUp} label="Receipts" value={fmtINR(s.receipts_total)} accent="bg-green-100 text-green-700" />
          <Stat icon={TrendDown} label="Payments" value={fmtINR(s.payments_total)} accent="bg-rose-100 text-rose-700" />
        </div>

        <Card className="rounded-sm border-border" data-testid="orders-geo-card">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <h3 className="font-heading font-semibold flex items-center gap-2"><MapPin size={18} weight="bold" /> Orders by {geo === "city" ? "City" : "State"}</h3>
            <div className="flex items-center gap-1 bg-secondary rounded-sm p-1">
              <Button size="sm" variant={geo === "city" ? "default" : "ghost"} className="rounded-sm h-7 px-3 text-xs" onClick={() => setGeo("city")} data-testid="geo-toggle-city">City-wise</Button>
              <Button size="sm" variant={geo === "state" ? "default" : "ghost"} className="rounded-sm h-7 px-3 text-xs" onClick={() => setGeo("state")} data-testid="geo-toggle-state">State-wise</Button>
            </div>
          </div>
          <div className="p-6 space-y-3" data-testid="orders-geo-list">
            {geoRows.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No orders yet</p>}
            {geoRows.map((r, i) => (
              <div key={i} data-testid={`geo-row-${i}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-medium">{r.name}</span>
                  <span className="text-muted-foreground">{r.orders} order(s) · {fmtINR(r.revenue)}</span>
                </div>
                <div className="h-2 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${(r.orders / maxOrders) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card className="rounded-sm border-border">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <h3 className="font-heading font-semibold">Recent Activity</h3>
            {s.pending_docs > 0 && (
              <span className="flex items-center gap-1.5 text-xs text-amber-700 font-medium">
                <Warning size={14} weight="bold" /> {s.pending_docs} transactions missing documents
              </span>
            )}
          </div>
          <div className="divide-y divide-border" data-testid="recent-activity">
            {s.recent_activity.length === 0 && (
              <p className="px-6 py-8 text-sm text-muted-foreground text-center">No activity yet</p>
            )}
            {s.recent_activity.map((a, i) => (
              <div key={i} className="px-6 py-3 flex items-center justify-between text-sm">
                <div className="flex items-center gap-3">
                  <span className="label-caps text-[9px] px-2 py-1 bg-secondary rounded-sm">{a.action}</span>
                  <span className="text-foreground">{a.detail}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {a.user_name} · {new Date(a.created_at).toLocaleString("en-IN")}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </>
  );
}
